import { Button, Loading, Select } from "@alm/flow-ds/components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";

import PageHeader from "components/PageHeader";
import Breadcrumb from "components/Breadcrumb";
import DashboardLayout from "components/Layouts/DashboardLayout";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import TasksList from "./Tasks/TasksList";
import Global404 from "components/Errors/Global404";
import VariablesTable from "components/VariablesTable";
import { ActivityInstance } from "records/ActivityInstance";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import api from "lib/api";
import DeleteDialog from "components/DeleteDialog";
import TerminateDialog from "../Modals/TerminateDialog";
import InfoBar from "components/InfoBar";
import { InfoBarColumn } from "components/InfoBar/InfoBar";
import { isAdmin } from "data/app/appSelectors";
import { useAlertsHandler } from "hooks/useAlertsHandler";
import ActivityInstancesError from "./ActivityInstancesError";
import CustomError from "lib/api/CustomError";
import { AxiosError } from "axios";
import { useGetConvertedUserTime } from "hooks/useTimezone";

interface RouteProps {
	id: string;
}

const ActivityInstanceDetailedView = () => {
	const { id } = useParams<RouteProps>();

	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();

	const admin = useSelector(isAdmin);

	const [instance, setInstance] = useState({} as ActivityInstance);

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const { ready } = usePagedData();

	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const [terminateModalOpen, setTerminateModalOpen] = useState(false);

	const { addErrorAlert } = useAlertsHandler();

	const [isUpdateInfoNeeded, setIsUpdateInfoNeeded] = useState(false);

	const [error, setError] = useState({} as CustomError);

	useEffect(() => {
		if (!init || isUpdateInfoNeeded) {
			// Load instances from API
			api.workflow
				.getActivityInstance(id)
				.then(result => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setInstance(result as unknown as ActivityInstance);
						ready();
						setInit(true);
					}
				})
				.catch(e => {
					console.error(e);

					const newError = e as AxiosError;
					const errorMessage: string | undefined =
						e.response?.data?.message;

					setError(
						new CustomError({
							httpCode: newError.response?.status,
							dump: newError,
							message:
								errorMessage !== undefined &&
								errorMessage.includes("Business Logic Error")
									? errorMessage
									: undefined,
						})
					);
				});
		}
	}, [_isMounted, init, ready, setInstance, id, isUpdateInfoNeeded]);

	const crumb = useMemo(
		() => [
			{
				name: t("main.activityInstances.headings.activityInstances"),
				link:
					(location.state as { from: string })?.from ||
					"/activity-instances",
			},
			{ name: (instance.workflow as string) || t("main.text.na") },
		],
		[instance.workflow, location.state, t]
	);

	const instanceData: InfoBarColumn[][] = [
		[
			{
				prop: t("main.activityInstances.headings.id"),
				value: instance.id,
				width: "md",
			},
			{
				prop: t("main.activityInstances.headings.workflow"),
				value: instance.workflow,
				width: "md",
			},
			{
				prop: t("main.activityInstances.headings.status"),
				value: instance.status,
				width: "sm",
			},
			{
				prop: t("main.activityInstances.headings.createdBy"),
				value: instance.createdBy,
				width: "md",
			},
			{
				prop: t("main.activityInstances.headings.created"),
				value: useGetConvertedUserTime(instance.created),
				width: "md",
			},
		],
	];

	if (instance.reason)
		instanceData.push([
			{
				prop: t("main.activityInstances.headings.reason"),
				value: instance.reason,
				width: "fill",
			},
		]);

	const handleSelectChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Select value changed to "${value}"`,
			});
			history.push({
				pathname: value,
				state: {
					from:
						(location.state as { from: string })?.from ||
						"/activity-instances",
				},
			});
		},
		[history, location.state]
	);

	const handleTerminate = async (reason: string) => {
		try {
			const response = await api.workflow.terminateActivityInstance(
				id,
				reason
			);
			if (response.status === 200) {
				setIsUpdateInfoNeeded(true);
			}
		} catch (e) {
			const error = e as AxiosError;
			console.error(error);
			addErrorAlert(error, "errorTerminatingActivityInstance");
			history.push("/activity-instances");
		}
	};

	const handleDelete = async () => {
		try {
			const response = await api.workflow.deleteActivityInstance(id);
			if (response.status === 200) {
				history.push("/activity-instances");
			}
		} catch (e) {
			const error = e as AxiosError;
			console.error(error);
			addErrorAlert(error, "errorDeletingActivityInstance");
			history.push("/activity-instances");
		}
	};

	if (Object.keys(error).length > 0)
		return (
			<DashboardLayout>
				<ActivityInstancesError error={error} />
			</DashboardLayout>
		);

	if (!init)
		return (
			<DashboardLayout>
				<Loading className="mt-4 elem__loader--md" />
			</DashboardLayout>
		);

	return (
		<DashboardLayout>
			{admin ? (
				<>
					<Breadcrumb crumb={crumb} className="container" />
					<div className="container">
						<PageHeader
							title={instance.workflow || t("main.text.na")}
							className="page-header__activity-instances header-container">
							<div className="activity-instances--button--container">
								{instance.status === "Active" && (
									<Button
										className="btn-outline-primary activity-instances--button__terminate"
										label={t(
											"main.activityInstances.labels.terminate"
										)}
										onClick={() =>
											setTerminateModalOpen(true)
										}
									/>
								)}
								<Button
									className="btn-primary activity-instances--button__delete"
									label={t(
										"main.activityInstances.labels.delete"
									)}
									onClick={() => setDeleteModalOpen(true)}
								/>

								<TerminateDialog
									isOpen={terminateModalOpen}
									setIsOpen={setTerminateModalOpen}
									terminateElement={handleTerminate}
									title={t(
										"main.activityInstances.headings.terminateActivity"
									)}
									content={t(
										"main.activityInstances.text.terminateActivity"
									)}
								/>
								<DeleteDialog
									isOpen={deleteModalOpen}
									setIsOpen={setDeleteModalOpen}
									deleteElement={handleDelete}
									title={t(
										"main.activityInstances.headings.deleteActivity"
									)}
									content={t(
										"main.activityInstances.text.deleteActivity"
									)}
								/>
							</div>
						</PageHeader>
						<InfoBar
							data={instanceData}
							showButton={false}
							stylesForRow="row-no-wrap"
						/>
						<div className="select-view--container">
							<Select
								value={location.pathname}
								onChange={handleSelectChange}
								label={t("main.tasks.labels.typeOfTasks")}
								labelHidden>
								<>
									<option
										value={`/activity-instance/${id}/tasks`}>
										{t(
											"main.activityInstances.headings.tasks"
										)}
										{` (${instance.taskCount})`}
									</option>
									<option
										value={`/activity-instance/${id}/variables`}>
										{t(
											"main.activityInstances.headings.variables"
										)}
										{` (${instance.variablesCount})`}
									</option>
								</>
							</Select>
						</div>
						<Switch>
							<Route
								exact
								path={[
									`/activity-instance/${id}`,
									`/activity-instance/${id}/tasks`,
								]}
								render={() => <TasksList instanceId={id} />}
							/>
							<Route
								exact
								path={`/activity-instance/${id}/variables`}
								render={() => (
									<VariablesTable
										getVariables={
											api.workflow
												.getActivityInstanceVariables
										}
										entityId={id}
									/>
								)}
							/>
							<Route component={Global404} />
						</Switch>
					</div>
				</>
			) : (
				<Redirect to="/403" />
			)}
		</DashboardLayout>
	);
};

export default ActivityInstanceDetailedView;
